import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Col, Modal, Row } from 'react-bootstrap';
import { Button as AntdButton, Modal as AntdModal } from 'antd';
import { saveAs } from 'file-saver';
import {
    FiArrowLeft,
    FiChevronDown,
    FiChevronUp,
    FiCheckCircle,
    FiDownload,
    FiEye,
    FiFileText,
    FiPaperclip,
} from 'react-icons/fi';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/button';
import CustomTabs from '../../components/CustomTabs';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import LogsComments from '../../components/LogsComments';
import NewTableComponent from '../../components/NewTableComponent';
import TripMetaGrid from '../../components/TripMetaGrid';
import TripRouteJourney from '../../components/TripRouteJourney';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { canViewTripRouteJourney, hasTripPermission, isSuperAdminRole } from '../../utils/helper';
import AttachmentViewerModal from '../../components/modals/AttachmentViewerModal';
import StatusModal from '../../components/modals/StatusModal';
import TripCloseModal from '../../components/modals/TripCloseModal';
import jHoldingLogo from '../../assets/images/JHolding.svg';
import { bigStatusIcon } from '../../utils/svgs';
import { errorNotify, successNotify } from '../../utils/toast';
import { apiService } from '../../services/apiService';
import { getTripDetails, setSelectedTripId } from '../../store/Slices/TripSlice';
import { fetchCostCenterDropdown, fetchCustomerDropdown } from '../../store/Slices/dropdownSlice';
import { TripPermission, TripsStageEnum } from '../../utils/app-constant';
import {
    buildClosingInitialValues,
    buildRouteJourneyStops,
    buildTripMetaProps,
    buildTripMetrics,
    formatCurrencyValue,
    formatDateOnly,
    formatNumberValue,
    getChecklistRows,
    getChecklistSummary,
    getCustomerAddress,
    getFirstValue,
    getPercentText,
    getRealTripId,
    getTripAttachments,
    getTripContractId,
    getTripContractName,
    getTripCostCenterId,
    getTripCostCenterName,
    getTripCustomerId,
    getTripIdDisplay,
    getTripReviewNotes,
    getRouteJourneySummary,
    joinContractCostCenter,
    normalizeNumber,
} from './tripDetailView.helpers';
import '../../styles/TripClosingActivity.css';

const logViewTabs = [
    { key: 'card', label: 'Card' },
    { key: 'table', label: 'Table' },
];


const formatClosingDateTime = (value) => {
    if (!value) return '';
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return value;

    return parsedDate.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).replace(',', '').replace(/\b(am|pm)\b/i, (match) => match.toUpperCase());
};

const validateClosingForm = (values) => {
    const errors = {};

    ['actual_start_datetime', 'actual_end_datetime', 'start_odometer', 'closing_odometer'].forEach((field) => {
        if (!String(values[field] || '').trim()) {
            errors[field] = 'Required';
        }
    });

    return errors;
};

const ToggleSwitch = ({ value, onChange }) => (
    <div className="closing_view_tabs">
        <CustomTabs
            tabs={logViewTabs}
            activeTab={value}
            setActiveTab={onChange}
        />
    </div>
);

const SummaryCard = ({ item }) => (
    <div className="closing_summary_item">
        <div className="closing_summary_title">{item.title}</div>
        <div className="closing_summary_value">
            {item.value}
            {item.badge ? (
                <span className={`closing_status_badge ${item.badgeTone || 'gold'}`}>
                    {item.badge}
                </span>
            ) : null}
        </div>
        <div className="closing_summary_footer">
            {item.footer === 'View Checklist' ? (
                <a href="#checklist">
                    <FiEye />
                    {item.footer}
                </a>
            ) : item.footer}
        </div>
    </div>
);

const ClosingField = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    required = false,
    unit,
    type = 'text',
    error,
    touched,
    disabled = false,
}) => (
    <Input
        label={label}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        showExtraText={Boolean(unit)}
        extraText={unit}
        required={required}
        error={error}
        touched={touched}
        disabled={disabled}
    />
);

const RemarksField = ({ values, setFieldValue, handleBlur, className = '', disabled = false }) => (
    <Input
        label="Remarks"
        placeholder="Write your remarks"
        type="text"
        name="remarks"
        value={values.remarks}
        setFieldValue={setFieldValue}
        onBlur={handleBlur}
        quill={true}
        required={false}
        className={`closing_description_input ${className}`.trim()}
        disabled={disabled}
    />
);

const ClosingReviewNote = ({ name, role, date, children }) => (
    <div className="closing_verification_note">
        <div className="closing_verification_note_header">
            <p>
                <strong>Remarks By:</strong>
                <a href="#remarks-by">{name}</a>
                {role ? <span>- {role}</span> : null}
            </p>
            {date ? <time>{date}</time> : null}
        </div>
        <p>{children}</p>
    </div>
);

const ClosingReviewNotes = ({ notes = [] }) => (
    <>
        {notes.map((note) => (
            <ClosingReviewNote key={note.id} name={note.name} role={note.role} date={note.date}>
                {note.text}
            </ClosingReviewNote>
        ))}
    </>
);

const LogTable = ({ type, rows = [], onOpenAttachment }) => {
    const isFuel = type === 'fuel';
    const safeRows = Array.isArray(rows) ? rows : [];
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const tableRows = safeRows.map((row, index) => {
        const files = row.files || (row.attachment ? [row.attachment] : []);
        const attachmentsCount = row.attachments !== undefined && row.attachments !== null
            ? Number(row.attachments)
            : (Array.isArray(files) ? files.length : 0);

        return {
            id: row.id || `${type}-log-${index + 1}`,
            date: row.date,
            location: isFuel ? row.tableLocation : (row.type || '--'),
            fuel: isFuel ? row.liters : '',
            cost: row.cost,
            attachments: attachmentsCount,
            files: files,
            raw: row.raw,
        };
    });

    const columns = isFuel ? [
        {
            name: 'Date',
            selector: (row) => row.date,
            cell: (row) => <span className="closing_log_table_text">{row.date || '--'}</span>,
            minWidth: '160px',
            grow: 1,
        },
        {
            name: 'Location',
            selector: (row) => row.location,
            cell: (row) => (
                <span className="closing_log_table_text closing_log_location_text" title={row.location || ''}>
                    {row.location || '--'}
                </span>
            ),
            minWidth: '320px',
            grow: 3,
            wrap: true,
        },
        {
            name: 'Fuel (Ltrs)',
            selector: (row) => row.fuel,
            cell: (row) => <span className="closing_log_table_text">{row.fuel || '--'}</span>,
            minWidth: '130px',
            grow: 1,
        },
        {
            name: 'Cost',
            selector: (row) => row.cost,
            cell: (row) => <span className="closing_log_table_text">{row.cost || '--'}</span>,
            minWidth: '120px',
            grow: 1,
        },
        {
            name: 'Attachments',
            selector: (row) => row.attachments,
            cell: (row) => {
                const files = row.files || [];
                const filesCount = row.attachments;
                if (filesCount > 0 && Array.isArray(files) && files.length > 0) {
                    return (
                        <button
                            type="button"
                            className="closing_attachment_count"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#5b21b6',
                                fontWeight: '600',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenAttachment?.(files);
                            }}
                            title="View attachments"
                        >
                            <FiPaperclip /> {filesCount}
                        </button>
                    );
                }
                return <span>-</span>;
            },
            minWidth: '130px',
            grow: 1,
        },
    ] : [
        {
            name: 'Date',
            selector: (row) => row.date,
            minWidth: '140px',
        },
        {
            name: 'Expense Type',
            selector: (row) => row.location,
            minWidth: '220px',
        },
        {
            name: 'Cost',
            selector: (row) => row.cost,
            minWidth: '120px',
        },
        {
            name: 'Attachments',
            selector: (row) => row.attachments,
            cell: (row) => {
                const files = row.files || [];
                const filesCount = row.attachments;
                if (filesCount > 0 && Array.isArray(files) && files.length > 0) {
                    return (
                        <button
                            type="button"
                            className="closing_attachment_count"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#5b21b6',
                                fontWeight: '600',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onOpenAttachment?.(files);
                            }}
                            title="View attachments"
                        >
                            <FiPaperclip /> {filesCount}
                        </button>
                    );
                }
                return <span>-</span>;
            },
            minWidth: '130px',
        },
    ];

    const totalPages = Math.max(Math.ceil(tableRows.length / limit), 1);
    const resolvedPage = Math.min(currentPage, totalPages);
    const paginatedRows = tableRows.slice((resolvedPage - 1) * limit, resolvedPage * limit);

    return (
        <div className="closing_log_table_wrapper">
            <NewTableComponent
                columns={columns}
                data={paginatedRows}
                currentPage={resolvedPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={(nextLimit) => {
                    setLimit(nextLimit);
                    setCurrentPage(1);
                }}
                totalItems={tableRows.length}
                totalPages={totalPages}
                showFilter={false}
                showAddBtn={false}
                tableClassName="closing_log_data_table"
                paginationClassName="closing_log_pagination"
                limitOptions={[10, 20, 50]}
                stickyColumns={0}
            />
        </div>
    );
};

const LogSection = ({ title, rate, mode, onModeChange, type, rows = [], onOpenAttachment, onOpenFuelSheet, fuelSheetLoading = false, canViewFuelSheet = false }) => {
    const isFuel = type === 'fuel';
    const safeRows = Array.isArray(rows) ? rows : [];

    return (
        <section className="closing_section_card closing_log_section">
            <div className="closing_log_header">
                <div className="closing_log_title_group">
                    <h3>{title}</h3>
                    {rate ? <span className="closing_rate_pill">{rate}</span> : null}
                </div>
                <div className="closing_log_header_actions">
                    {isFuel && canViewFuelSheet ? (
                        <button
                            type="button"
                            className="closing_fuel_sheet_btn"
                            onClick={onOpenFuelSheet}
                            disabled={fuelSheetLoading}
                            title="View Fuel Sheet PDF"
                        >
                            {fuelSheetLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    <span>Loading...</span>
                                </>
                            ) : (
                                <>
                                    <FiFileText />
                                    <span>Fuel Sheet</span>
                                </>
                            )}
                        </button>
                    ) : null}
                    <ToggleSwitch value={mode} onChange={onModeChange} />
                </div>
            </div>

            {mode === 'table' ? (
                <LogTable type={type} rows={safeRows} onOpenAttachment={onOpenAttachment} />
            ) : (
                <div className="closing_log_cards">
                    {safeRows.length > 0 ? (
                        safeRows.map((row, index) => {
                            const files = row.files || (row.attachment ? [row.attachment] : []);
                            const hasAttachments = row.attachments > 0 && files.length > 0;

                            return (
                                <div className="closing_log_card" key={row.id || `${type}-card-${index}`}>
                                    <div>
                                        <h4>{isFuel ? row.location : row.type}</h4>
                                        <div className="closing_log_meta">
                                            {hasAttachments ? (
                                                <button
                                                    type="button"
                                                    className="closing_attachment_chip"
                                                    style={{ border: 'none', cursor: 'pointer', background: '#f1f5f9', color: '#5b21b6', padding: '4px 8px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                                    onClick={() => onOpenAttachment?.(files)}
                                                    title="View attachments"
                                                >
                                                    <FiPaperclip /> {row.attachments} Attachment{row.attachments > 1 ? 's' : ''}
                                                </button>
                                            ) : null}
                                            <span>{row.displayDate}</span>
                                        </div>
                                    </div>
                                    <div className="closing_log_amount">
                                        {isFuel ? (
                                            <>
                                                <span>{row.liters} Liters</span>
                                                <strong>{row.cost === '--' ? '--' : `${row.cost} PKR`}</strong>
                                            </>
                                        ) : (
                                            <strong>{row.cardCost}</strong>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="closing_empty_state">
                            No {isFuel ? 'fuel' : 'expense'} logs found.
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

const CostSummary = ({ metrics = {} }) => (
    <div className="service_cost_summary mb-3">
        <h6>Cost Summary</h6>
        <p>Financial breakdown of the trip.</p>
        <div className="service_item">
            <div>Allocated Petty Cash</div>
            <div>{formatCurrencyValue(metrics.totalPettyCashDisplayValue)}</div>
        </div>
        <div className="service_item">
            <div>Fuel Cost</div>
            <div>{formatCurrencyValue(metrics.fuelCost)}</div>
        </div>
        <div className="service_item">
            <div>Expense Cost</div>
            <div>{formatCurrencyValue(metrics.totalExpenseAmount)}</div>
        </div>
        <div className="service_item">
            <div>Balanced Petty Cash</div>
            <div>{formatCurrencyValue(metrics.remainingPettyCashAmount)}</div>
        </div>
        <div className="total">
            <div>Total Amount</div>
            <span>{formatCurrencyValue(metrics.totalSpendAmount)}</span>
        </div>
    </div>
);

const GenerateInvoiceButton = ({ className = '', onClick, loading = false }) => (
    <Button
        iconButton
        icon={<FiDownload />}
        iconBtnText={loading ? 'Downloading...' : 'Download Report'}
        onClick={onClick}
        disabled={loading}
        loading={loading}
        type="button"
        shouldRow={false}
        buttonExtraCss={`closing_generate_invoice_btn ${className}`.trim()}
    />
);

const FinancialReview = ({ tripIdDisplay, rows = [] }) => (
    <section className="closing_panel invoicing_financial_review">
        <div className="closing_panel_header invoicing_financial_header">
            <div>
                <h3>Financial Review</h3>
                <p>Review trip expenses, allocated costs, and billing information before processing the invoice.</p>
            </div>
            <div className="trip_id_green_pill">
                Trip ID: {tripIdDisplay}
            </div>
        </div>

        <div className="invoicing_financial_rows">
            {rows.map((item) => (
                <div
                    className={`invoicing_financial_row ${item.separated ? 'is_separated' : ''} ${item.emphasis ? 'is_emphasis' : ''}`.trim()}
                    key={item.label}
                >
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                </div>
            ))}
        </div>
    </section>
);

const InvoiceRemarksField = ({ values, setFieldValue, handleBlur }) => (
    <section className="closing_remarks invoicing_remarks_card">
        <Input
            label="Remarks"
            placeholder="Write your remarks"
            type="text"
            name="invoice_remarks"
            value={values.invoice_remarks}
            setFieldValue={setFieldValue}
            onBlur={handleBlur}
            quill={true}
            required={false}
            className="invoicing_remarks_input"
        />
    </section>
);

const GeneratedInvoiceModal = ({ show, onHide, tripIdDisplay, invoiceData = {}, onDownload, downloading = false }) => (
    <Modal
        show={show}
        onHide={onHide}
        centered
        size="xl"
        dialogClassName="generated_invoice_dialog"
        contentClassName="generated_invoice_modal_content"
    >
        <Modal.Body>
            <div className="generated_invoice_modal_header">
                <h2>Generated Invoice</h2>
                <Button
                    iconButton
                    icon={<FiDownload />}
                    iconBtnText={downloading ? 'Downloading...' : 'Download PDF'}
                    onClick={onDownload}
                    disabled={downloading}
                    loading={downloading}
                    type="button"
                    shouldRow={false}
                    buttonExtraCss="generated_invoice_download_btn"
                />
            </div>

            <section className="generated_invoice_document">
                <div className="generated_invoice_top">
                    <div className="generated_invoice_brand">
                        <img src={jHoldingLogo} alt="J Holdings" />
                        <h3>Trip Invoice</h3>
                    </div>

                    <div className="generated_invoice_title_block">
                        <h1>INVOICE</h1>
                        <p>
                            <span>Invoice No:</span>
                            <strong>{invoiceData.invoiceNo || `INV-${tripIdDisplay}`}</strong>
                        </p>
                        <p>
                            <span>Date:</span>
                            <strong>{invoiceData.invoiceDate || '--'}</strong>
                        </p>
                    </div>
                </div>

                <div className="generated_invoice_divider" />

                <div className="generated_invoice_info_grid">
                    <div className="generated_invoice_bill_to">
                        <span>Bill To:</span>
                        <strong>{invoiceData.billToName || '--'}</strong>
                        <p>{invoiceData.billToAddress || '--'}</p>
                    </div>

                    <div className="generated_invoice_trip_details">
                        {(invoiceData.tripDetails || []).map((item) => (
                            <div className="generated_invoice_trip_row" key={item.label}>
                                <span>{item.label}</span>
                                <strong>{item.value || '--'}</strong>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="generated_invoice_table_wrap">
                    <table className="generated_invoice_table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Description</th>
                                <th>Category</th>
                                <th>Amount (PKR)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(invoiceData.lineItems || []).length > 0 ? (
                                invoiceData.lineItems.map((item) => (
                                    <tr key={item.number}>
                                        <td>{item.number}</td>
                                        <td>{item.description}</td>
                                        <td>{item.category}</td>
                                        <td>{item.amount}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4}>No billing items found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="generated_invoice_totals">
                    <div className="generated_invoice_total_line">
                        <span>Sub Total</span>
                        <strong>{invoiceData.subTotal || '--'}</strong>
                    </div>
                    <div className="generated_invoice_total_line">
                        <span>{invoiceData.taxLabel || 'Tax'}</span>
                        <strong>{invoiceData.taxAmount || '--'}</strong>
                    </div>
                    <div className="generated_invoice_total_divider" />
                    <div className="generated_invoice_grand_total">
                        <span>Grand Total</span>
                        <strong>{invoiceData.grandTotal || '--'}</strong>
                    </div>
                </div>
            </section>
        </Modal.Body>
    </Modal>
);

const ReturnForCorrectionModal = ({
    open,
    onClose,
    onConfirm,
    tripIdDisplay,
    loading,
}) => {
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setNotes('');
            setError('');
        }
    }, [open]);

    const handleConfirm = () => {
        const trimmed = String(notes || '').trim();
        if (!trimmed) {
            setError('Correction notes are required');
            return;
        }
        onConfirm(trimmed);
    };

    const footer = (
        <>
            <AntdButton onClick={onClose} disabled={loading}>
                Cancel
            </AntdButton>
            <AntdButton
                type="primary"
                onClick={handleConfirm}
                className="closing_return_correction_modal_btn"
                style={{
                    backgroundColor: '#ffd8d8',
                    borderColor: '#ffd8d8',
                    color: '#ff0000',
                    fontWeight: 700,
                }}
                loading={loading}
                disabled={loading}
            >
                Return for Correction
            </AntdButton>
        </>
    );

    return (
        <AntdModal
            footer={footer}
            title={bigStatusIcon}
            mask={{ enabled: true, blur: true }}
            open={open}
            onCancel={onClose}
            centered={true}
            destroyOnClose
            className="status_action_modal return_correction_custom_modal"
        >
            <div className="my-4 status_modal_body_content">
                <h3 className="status_modal_title">Return this trip for correction?</h3>
                <p className="status_modal_desc">This will change Trip {tripIdDisplay} stage to Correction and status to In Transit.</p>
                <div className="mt-3">
                    <label className="correction_notes_label">
                        Correction Notes <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                        className="form-control correction_notes_textarea"
                        rows={3}
                        placeholder="Please provide reason/notes for returning this trip..."
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value);
                            if (error && e.target.value.trim()) setError('');
                        }}
                        disabled={loading}
                        style={{
                            borderColor: error ? '#ef4444' : '#cbd5e1',
                        }}
                    />
                    {error && (
                        <div className="correction_notes_error_msg">
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </AntdModal>
    );
};

const TripHistoryApprovalTrail = ({ isOpen, onToggle, children }) => (
    <section className={`invoicing_history_trail ${isOpen ? 'is_open' : ''}`.trim()}>
        <button
            aria-expanded={isOpen}
            className="invoicing_history_toggle"
            onClick={onToggle}
            type="button"
        >
            <span className="invoicing_history_icon">
                {isOpen ? <FiChevronUp /> : <FiChevronDown />}
            </span>
            <span className="invoicing_history_title_group">
                <strong>Trip History &amp; Approval Trail</strong>
                <small>Explore the chronological history of the trip, including approvals, remarks, and milestone updates.</small>
            </span>
        </button>

        {isOpen ? (
            <div className="invoicing_history_content">
                {children}
            </div>
        ) : null}
    </section>
);

const InvoicingApprovalTrailContent = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    attachments,
    setAttachments,
    fuelViewMode,
    setFuelViewMode,
    expenseViewMode,
    setExpenseViewMode,
    metaProps,
    routeStops,
    distanceSummary,
    canViewRouteJourney,
    dispatchSummary,
    tripCostSummary,
    fuelLogs,
    expenseLogs,
    fuelRateLabel,
    reviewNotes,
    onOpenAttachment,
    onOpenFuelSheet,
    fuelSheetLoading,
    canViewFuelSheet,
}) => (
    <>
        <ClosingReviewNotes notes={reviewNotes} />

        <TripMetaGrid includeContractCostCenter {...metaProps} />

        {canViewRouteJourney && (
            <TripRouteJourney
                className="closing_route_journey"
                stops={routeStops}
                distanceSummary={distanceSummary}
            />
        )}

        <section className="closing_section_card">
            <h3>Dispatch Summary</h3>
            <div className="closing_summary_grid dispatch_grid">
                {(dispatchSummary || []).map((item) => (
                    <SummaryCard item={item} key={item.title} />
                ))}
            </div>
        </section>

        <section className="closing_section_card">
            <h3>Trip Cost Summary</h3>
            <div className="closing_summary_grid">
                {(tripCostSummary || []).map((item) => (
                    <SummaryCard item={item} key={item.title} />
                ))}
            </div>
        </section>

        <LogSection
            title="Fuel Log"
            rate={fuelRateLabel}
            mode={fuelViewMode}
            onModeChange={setFuelViewMode}
            type="fuel"
            rows={fuelLogs}
            onOpenAttachment={onOpenAttachment}
            onOpenFuelSheet={onOpenFuelSheet}
            fuelSheetLoading={fuelSheetLoading}
            canViewFuelSheet={canViewFuelSheet}
        />

        <LogSection
            title="Expense Log"
            mode={expenseViewMode}
            onModeChange={setExpenseViewMode}
            type="expense"
            rows={expenseLogs}
            onOpenAttachment={onOpenAttachment}
        />

        <section className="closing_section_card closing_closing_card">
            <h3>Closing Activity</h3>
            <div className="closing_fields_grid">
                <ClosingField
                    label="Actual Start Date & Time"
                    name="actual_start_datetime"
                    type="text"
                    value={formatClosingDateTime(values.actual_start_datetime)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={errors.actual_start_datetime}
                    touched={touched.actual_start_datetime}
                    disabled
                />
                <ClosingField
                    label="Actual End Date & Time"
                    name="actual_end_datetime"
                    type="text"
                    value={formatClosingDateTime(values.actual_end_datetime)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={errors.actual_end_datetime}
                    touched={touched.actual_end_datetime}
                    disabled
                />
                <ClosingField
                    label="Start Odometer Reading"
                    name="start_odometer"
                    value={values.start_odometer}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    unit="km"
                    error={errors.start_odometer}
                    touched={touched.start_odometer}
                    disabled
                />
                <ClosingField
                    label="Closing Odometer Reading"
                    name="closing_odometer"
                    value={values.closing_odometer}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    unit="km"
                    error={errors.closing_odometer}
                    touched={touched.closing_odometer}
                    disabled
                />
                <ClosingField
                    label="Opening Fuel"
                    name="opening_fuel"
                    value={values.opening_fuel}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    unit="ltrs"
                    disabled
                />
                <ClosingField
                    label="Closing Fuel"
                    name="closing_fuel"
                    value={values.closing_fuel}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    unit="ltrs"
                    disabled
                />
            </div>

            <h4 className="product_shortage_title">Product Shortage</h4>
            <div className="product_shortage_grid">
                <ClosingField
                    label="Delivered Quantity"
                    name="delivered_qty"
                    value={values.delivered_qty || values.delivered || '0.00'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    unit="Items"
                    disabled
                />
                <ClosingField
                    label="Shortage"
                    name="shortage"
                    value={values.shortage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    unit="Items"
                    disabled
                />
                <ClosingField
                    label="Relief Qty"
                    name="relief_qty"
                    value={values.relief_qty}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    unit="Items"
                    disabled
                />
            </div>
        </section>

        <section className="closing_section_card closing_attachments_card invoicing_archive_attachments_card">
            <h3>Trip Attachments</h3>
            <FileUpload
                destination="trip-closing"
                files={attachments}
                setFiles={setAttachments}
                maxFileSize={10}
                className="closing_file_upload invoicing_archive_file_upload"
                disabled
            />
        </section>
    </>
);

const TripClosingActivity = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const realTripId = useMemo(() => getRealTripId(id), [id]);
    const { tripDetailsData, tripDetailsLoading } = useSelector((state) => state.trips);
    const { customerDropdownData, costCenterDropdownData } = useSelector((state) => state.dropdowns || state.dropdown || {});
    const detailData = useMemo(() => tripDetailsData?.data || tripDetailsData || {}, [tripDetailsData]);
    const [isVerificationMode, setIsVerificationMode] = useState(false);
    const [isApprovalMode, setIsApprovalMode] = useState(false);
    const [isInvoicingMode, setIsInvoicingMode] = useState(false);
    const [isApprovalTrailOpen, setIsApprovalTrailOpen] = useState(false);
    const [showGeneratedInvoiceModal, setShowGeneratedInvoiceModal] = useState(false);
    const [verifyTripModalOpen, setVerifyTripModalOpen] = useState(false);
    const [verifyTripLoading, setVerifyTripLoading] = useState(false);
    const [returnCorrectionModalOpen, setReturnCorrectionModalOpen] = useState(false);
    const [returnCorrectionLoading, setReturnCorrectionLoading] = useState(false);
    const [approveTripModalOpen, setApproveTripModalOpen] = useState(false);
    const [approveTripLoading, setApproveTripLoading] = useState(false);
    const [tripCloseDetailData, setTripCloseDetailData] = useState(null);
    const [tripCloseDetailLoading, setTripCloseDetailLoading] = useState(false);
    const [settlementData, setSettlementData] = useState(null);
    const [settlementLoading, setSettlementLoading] = useState(false);
    const [downloadingReport, setDownloadingReport] = useState(false);
    const [fuelSheetLoading, setFuelSheetLoading] = useState(false);
    const [builtyLoading, setBuiltyLoading] = useState(false);
    const [podLoading, setPodLoading] = useState(false);
    const [fuelViewMode, setFuelViewMode] = useState('card');
    const [expenseViewMode, setExpenseViewMode] = useState('card');
    const [attachments, setAttachments] = useState([]);
    const [attachmentViewerFiles, setAttachmentViewerFiles] = useState([]);
    const [showAttachmentViewerModal, setShowAttachmentViewerModal] = useState(false);

    const handleOpenAttachmentViewer = (files = []) => {
        const normalizedFiles = Array.isArray(files) ? files : (files ? [files] : []);
        if (!normalizedFiles.length) return;
        setAttachmentViewerFiles(normalizedFiles);
        setShowAttachmentViewerModal(true);
    };
    const [tripDetailsRequestPending, setTripDetailsRequestPending] = useState(() => Boolean(realTripId));
    const [loadedTripDetailsId, setLoadedTripDetailsId] = useState(null);
    const [fetchedCustomerName, setFetchedCustomerName] = useState('');
    const [customerLocations, setCustomerLocations] = useState([]);
    const [customerContracts, setCustomerContracts] = useState([]);
    const [fetchedContractName, setFetchedContractName] = useState('');
    const requestedContractIdRef = useRef('');

    useEffect(() => {
        if (!detailData || typeof detailData !== 'object') return;
        const stage = String(detailData.stage || detailData?.data?.stage || '').toLowerCase().trim();
        const status = String(detailData.status || detailData?.data?.status || '').toLowerCase().trim();

        if (stage === 'closed' || status === 'closed' || stage === TripsStageEnum.Closed) {
            setIsInvoicingMode(true);
            setIsApprovalMode(false);
            setIsVerificationMode(false);
            setIsApprovalTrailOpen(false);
        } else if (stage === 'verified' || stage === TripsStageEnum.Verified) {
            setIsApprovalMode(true);
            setIsVerificationMode(true);
            setIsInvoicingMode(false);
            setIsApprovalTrailOpen(false);
        } else if (stage === 'unverified' || stage === TripsStageEnum.Unverified) {
            setIsInvoicingMode(false);
            setIsApprovalMode(false);
            setIsVerificationMode(true);
            setIsApprovalTrailOpen(false);
        } else {
            setIsInvoicingMode(false);
            setIsApprovalMode(false);
            setIsVerificationMode(false);
            setIsApprovalTrailOpen(false);
        }
    }, [detailData]);

    useEffect(() => {
        let isActive = true;

        if (!realTripId) {
            setTripDetailsRequestPending(false);
            setLoadedTripDetailsId(null);
            return () => {
                isActive = false;
            };
        }

        setTripDetailsRequestPending(true);
        dispatch(getTripDetails(realTripId)).finally(() => {
            if (isActive) {
                setLoadedTripDetailsId(realTripId);
                setTripDetailsRequestPending(false);
            }
        });
        dispatch(setSelectedTripId(realTripId));
        dispatch(fetchCustomerDropdown());
        dispatch(fetchCostCenterDropdown());

        return () => {
            isActive = false;
        };
    }, [dispatch, realTripId]);

    useEffect(() => {
        const tripNumericId = Number(realTripId || detailData?.id || id);
        if (!tripNumericId || !isInvoicingMode) return;

        let isActive = true;
        setSettlementLoading(true);
        apiService
            .getTripSettlementDetail(tripNumericId)
            .then((res) => {
                if (isActive) {
                    setSettlementData(res?.data || res || null);
                }
            })
            .catch((err) => {
                console.error('Error fetching trip settlement detail:', err);
            })
            .finally(() => {
                if (isActive) {
                    setSettlementLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [isInvoicingMode, realTripId, detailData?.id, id]);

    useEffect(() => {
        setAttachments(getTripAttachments(detailData));
    }, [detailData]);

    useEffect(() => {
        let isActive = true;

        const fetchCustomerDetailData = async () => {
            const customerId = getTripCustomerId(detailData);
            setFetchedCustomerName('');
            setCustomerLocations([]);
            setCustomerContracts([]);

            if (!customerId) return;

            try {
                const response = await apiService.getCustomerTripDetail(customerId);
                const data = response?.data || response || {};
                const customerName = getFirstValue(data, [
                    'name',
                    'label',
                    'title',
                    'customer_name',
                    'customerName',
                    'company_name',
                    'companyName',
                    'display_name',
                    'displayName',
                    'customer.name',
                    'customer.label',
                    'customer.customer_name',
                    'customer.company_name',
                    'data.name',
                    'data.label',
                    'data.customer_name',
                    'data.company_name',
                    'data.customer.name',
                    'data.customer.label',
                ]);

                if (isActive && customerName) {
                    setFetchedCustomerName(customerName);
                }

                const locations = data?.locations ||
                    data?.warehouses ||
                    data?.customer_locations ||
                    data?.customer_warehouses ||
                    data?.data?.locations ||
                    data?.data?.warehouses ||
                    data?.data?.customer_locations ||
                    data?.data?.customer_warehouses ||
                    [];
                const formattedLocations = (Array.isArray(locations) ? locations : []).map((location) => {
                    const locationName = location?.warehouse_name ||
                        location?.name ||
                        location?.label ||
                        location?.title ||
                        location?.location_name ||
                        location?.point_name ||
                        location?.warehouse?.warehouse_name ||
                        location?.warehouse?.name ||
                        location?.address ||
                        `Location #${location?.id || location?.value || ''}`;
                    const cityOrAddress = location?.city ||
                        location?.warehouse?.city ||
                        (location?.address && location.address !== locationName ? location.address : '');
                    const label = [locationName, cityOrAddress].filter(Boolean).join(' - ');

                    return {
                        value: String(location?.id || location?.value || location?.customer_warehouse_id || location?.warehouse_id || location?.warehouse?.id || ''),
                        id: location?.id,
                        customer_warehouse_id: location?.customer_warehouse_id,
                        warehouse_id: location?.warehouse_id || location?.warehouse?.id,
                        name: locationName,
                        label,
                        raw: location,
                    };
                });

                const contracts = data?.contracts || data?.data?.contracts || [];
                const formattedContracts = (Array.isArray(contracts) ? contracts : []).map((contract) => ({
                    value: String(contract?.id ?? contract?.value ?? contract?.contract_id ?? ''),
                    label: String(
                        contract?.name ||
                        contract?.label ||
                        contract?.title ||
                        contract?.contract_name ||
                        contract?.contract_no ||
                        ''
                    ).trim(),
                })).filter((option) => option.value);

                if (isActive) {
                    setCustomerLocations(formattedLocations);
                    setCustomerContracts(formattedContracts);
                }
            } catch (error) {
                console.error('Error fetching customer trip detail:', error);
            }
        };

        fetchCustomerDetailData();

        return () => {
            isActive = false;
        };
    }, [detailData]);

    const tripIdDisplay = getTripIdDisplay(detailData, id);
    const dispatchTripId = realTripId || detailData?.id || id;
    const customerId = getTripCustomerId(detailData);
    const dropdownCustomerName = useMemo(() => {
        if (!customerId || !Array.isArray(customerDropdownData)) return '';

        const matchedCustomer = customerDropdownData.find((customer) =>
            String(customer?.value || customer?.id || customer?.customer_id) === String(customerId)
        );

        return matchedCustomer?.label ||
            matchedCustomer?.name ||
            matchedCustomer?.title ||
            matchedCustomer?.customer_name ||
            '';
    }, [customerDropdownData, customerId]);
    const contractId = getTripContractId(detailData);
    const costCenterId = getTripCostCenterId(detailData);
    const contractLabel = useMemo(() => {
        const detailContractName = String(getTripContractName(detailData) || '').trim();
        if (detailContractName) return detailContractName;

        const matchedContract = (Array.isArray(customerContracts) ? customerContracts : [])
            .find((option) => String(option.value) === String(contractId));

        return matchedContract?.label || fetchedContractName;
    }, [contractId, customerContracts, detailData, fetchedContractName]);
    const costCenterLabel = useMemo(() => {
        const detailCostCenterName = String(getTripCostCenterName(detailData) || '').trim();
        if (detailCostCenterName) return detailCostCenterName;
        if (!costCenterId || !Array.isArray(costCenterDropdownData)) return '';

        const matchedCostCenter = costCenterDropdownData.find((costCenter) =>
            String(costCenter?.value ?? costCenter?.id ?? '') === String(costCenterId)
        );

        return String(
            matchedCostCenter?.label ||
            matchedCostCenter?.title ||
            matchedCostCenter?.name ||
            ''
        ).trim();
    }, [costCenterDropdownData, costCenterId, detailData]);
    const metaProps = useMemo(() => {
        const resolvedMetaProps = buildTripMetaProps(detailData);
        return {
            ...resolvedMetaProps,
            customerName: fetchedCustomerName ||
                dropdownCustomerName ||
                (resolvedMetaProps.customerName === '--' && customerId ? `Customer #${customerId}` : resolvedMetaProps.customerName),
            contractCostCenter: joinContractCostCenter(contractLabel, costCenterLabel),
        };
    }, [contractLabel, costCenterLabel, customerId, detailData, dropdownCustomerName, fetchedCustomerName]);

    useEffect(() => {
        let isActive = true;

        if (!contractId) {
            requestedContractIdRef.current = '';
            setFetchedContractName('');
            return () => {
                isActive = false;
            };
        }

        const detailContractName = String(getTripContractName(detailData) || '').trim();
        const matchedContract = (Array.isArray(customerContracts) ? customerContracts : [])
            .find((option) => String(option.value) === String(contractId));

        if (detailContractName || matchedContract?.label) {
            return () => {
                isActive = false;
            };
        }

        if (requestedContractIdRef.current === String(contractId)) {
            return () => {
                isActive = false;
            };
        }

        requestedContractIdRef.current = String(contractId);
        setFetchedContractName('');

        const contractNamePaths = [
            'name',
            'label',
            'title',
            'contract_name',
            'contract_no',
            'contract_number',
            'contract.name',
            'contract.label',
            'contract.title',
            'contract.contract_name',
            'data.name',
            'data.label',
            'data.contract_name',
        ];

        const readContractName = async (request) => {
            try {
                const response = await request(contractId);
                return String(getFirstValue(response?.data || response || {}, contractNamePaths) || '').trim();
            } catch (error) {
                console.error('Error fetching contract detail:', error);
                return '';
            }
        };

        const fetchContractName = async () => {
            const contractName = await readContractName(apiService.getContractTripDetail) ||
                await readContractName(apiService.getContractDetail);

            if (isActive && contractName) {
                setFetchedContractName(contractName);
            }
        };

        fetchContractName();

        return () => {
            isActive = false;
        };
    }, [contractId, customerContracts, detailData]);
    const routeStops = useMemo(() => buildRouteJourneyStops(detailData, customerLocations), [customerLocations, detailData]);
    const distanceSummary = useMemo(() => getRouteJourneySummary(detailData, routeStops), [detailData, routeStops]);
    const canViewRouteJourney = useMemo(() => canViewTripRouteJourney(), []);
    const canViewFuelSheet = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.FuelSheet, 'fuel-sheet', 'Fuel Sheet', 'fuel_sheet']), []);
    const canViewBuilty = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.Builty, 'Builty', 'builty', 'bilty', 'Bilty', 'trip-builty']), []);
    const canViewPod = useMemo(() => isSuperAdminRole() || hasTripPermission([TripPermission.POD, 'POD', 'pod', 'Pod', 'trip-pod', 'Trip-POD']), []);
    const canVerifyTrip = useMemo(() => {
        return (
            isSuperAdminRole() ||
            hasTripPermission([
                TripPermission.Verify,
                "verify",
                "trip-verify",
                "trip_verify",
            ])
        );
    }, []);
    const canApproveTrip = useMemo(() => {
        return (
            isSuperAdminRole() ||
            hasTripPermission([
                TripPermission.Approved,
                "approved",
                "approve",
                "trip-approved",
                "trip_approved",
            ])
        );
    }, []);
    const canReturnForCorrection = useMemo(() => {
        return (
            isSuperAdminRole() ||
            hasTripPermission([
                TripPermission.ReturnToCorrection,
                TripPermission.Correction,
                "return-to-correction",
                "correction",
                "trip-correction",
                "trip_correction",
            ])
        );
    }, []);
    const canSubmitClosingActivity = useMemo(() => {
        return (
            isSuperAdminRole() ||
            hasTripPermission([
                TripPermission.Completed,
                TripPermission.Update,
                TripPermission.Create,
                "completed",
                "update",
                "create",
            ])
        );
    }, []);
    const reviewNotes = useMemo(() => getTripReviewNotes(detailData), [detailData]);
    const pageTitle = isInvoicingMode
        ? 'Invoicing'
        : isApprovalMode
            ? 'Approval Summary'
            : isVerificationMode
                ? 'Verification Summary'
                : 'Closing Activity';
    const metrics = useMemo(() => buildTripMetrics(detailData), [detailData]);
    const initialValues = useMemo(() => buildClosingInitialValues(detailData), [detailData]);
    const vehicleChecklistRows = useMemo(() => getChecklistRows(detailData, [
        'vehicle_inspection_checklist',
        'vehicle_inspection_checklists',
        'vehicleInspectionChecklist',
        'trip_vehicle_inspection_checklist',
    ]), [detailData]);
    const driverDocRows = useMemo(() => getChecklistRows(detailData, [
        'driver_doc_checklist',
        'driver_doc_checklists',
        'driverDocumentChecklist',
        'trip_driver_doc_checklist',
    ]), [detailData]);
    const vehicleSummary = useMemo(() => getChecklistSummary(vehicleChecklistRows), [vehicleChecklistRows]);
    const driverDocSummary = useMemo(() => getChecklistSummary(driverDocRows), [driverDocRows]);
    const fuelLogs = metrics.fuelLogs || [];
    const expenseLogs = metrics.expenseLogs || [];
    const fuelRateLabel = normalizeNumber(metrics.fuelRate) !== null
        ? `${formatNumberValue(metrics.fuelRate)} PKR/Ltr`
        : '';
    const openingFuel = getFirstValue(detailData, ['opening_fuel', 'openingFuel']);
    const allocatedFuel = getFirstValue(detailData, ['allocated_fuel', 'allocatedFuel']);
    const dispatchSummary = useMemo(() => {
        const buildChecklistCard = (title, summary) => {
            const isComplete = summary.total > 0 && summary.passed === summary.total;
            return {
                title,
                value: summary.total ? `${summary.passed}/${summary.total}` : '--',
                badge: summary.total ? (isComplete ? 'Complete' : 'Incomplete') : null,
                badgeTone: isComplete ? 'green' : 'amber',
                footer: summary.total ? `${summary.failed + summary.pending} pending` : 'No checklist items',
            };
        };

        return [
            buildChecklistCard('Vehicle Inspection', vehicleSummary),
            buildChecklistCard('Driver Documents', driverDocSummary),
            {
                title: 'Allocated Fuel',
                value: `${formatNumberValue(allocatedFuel)} L`,
                footer: `Opening Fuel - ${formatNumberValue(openingFuel)} L`,
            },
            {
                title: 'Allocated Petty Cash',
                value: formatCurrencyValue(metrics.totalPettyCashDisplayValue),
                footer: (
                    <>
                        Remaining Balance - <strong>{formatCurrencyValue(metrics.remainingPettyCashAmount)}</strong>
                    </>
                ),
            },
        ];
    }, [allocatedFuel, driverDocSummary, metrics.remainingPettyCashAmount, metrics.totalPettyCashDisplayValue, openingFuel, vehicleSummary]);

    const tripCostSummary = useMemo(() => ([
        {
            title: 'Fuel Filled',
            value: `${formatNumberValue(metrics.totalFilledFuel)} L`,
            footer: `${fuelLogs.length} Fill-up${fuelLogs.length === 1 ? '' : 's'}`,
        },
        {
            title: 'Fuel Cost',
            value: formatCurrencyValue(metrics.fuelCost),
            badge: fuelRateLabel,
            footer: `Across ${fuelLogs.length} ${fuelLogs.length === 1 ? 'Entry' : 'Entries'}`,
        },
        {
            title: 'Total Expenses',
            value: formatCurrencyValue(metrics.totalExpenseAmount),
            footer: `${expenseLogs.length} ${expenseLogs.length === 1 ? 'Entry' : 'Entries'}`,
        },
        {
            title: 'Remaining Petty Cash',
            value: formatCurrencyValue(metrics.remainingPettyCashAmount),
            footer: (
                <>
                    <strong>{getPercentText(metrics.totalExpenseAmount, metrics.totalPettyCashDisplayValue)}</strong> of allocation used
                </>
            ),
        },
    ]), [
        expenseLogs.length,
        fuelLogs.length,
        fuelRateLabel,
        metrics.fuelCost,
        metrics.remainingPettyCashAmount,
        metrics.totalExpenseAmount,
        metrics.totalFilledFuel,
        metrics.totalPettyCashDisplayValue,
    ]);

    const invoiceData = useMemo(() => {
        const actualStartAt = getFirstValue(detailData, ['actual_start_at', 'started_at', 'dispatch_at', 'start_at', 'created_at']);
        const actualEndAt = getFirstValue(detailData, ['actual_end_at', 'completed_at', 'ended_at', 'end_at', 'updated_at']);
        const firstStop = routeStops[0];
        const lastStop = routeStops[routeStops.length - 1];
        const subtotalValue = normalizeNumber(metrics.totalSpendAmount) || 0;
        const taxValue = getFirstValue(detailData, [
            'gst_amount',
            'tax_amount',
            'invoice.gst_amount',
            'invoice.tax_amount',
        ], 0);
        const taxPercent = getFirstValue(detailData, [
            'gst_percent',
            'gst_percentage',
            'tax_percent',
            'tax_percentage',
            'invoice.gst_percent',
            'invoice.tax_percent',
        ]);
        const grandTotalValue = getFirstValue(detailData, [
            'grand_total',
            'invoice_total',
            'invoice.grand_total',
            'invoice.total_amount',
        ], subtotalValue + (normalizeNumber(taxValue) || 0));
        const lineItems = [];

        if ((normalizeNumber(metrics.fuelCost) || 0) > 0 || fuelLogs.length > 0) {
            lineItems.push({
                number: String(lineItems.length + 1),
                description: 'Fuel Log',
                category: 'Fuel & Operations',
                amount: formatCurrencyValue(metrics.fuelCost),
            });
        }

        if ((normalizeNumber(metrics.totalExpenseAmount) || 0) > 0 || expenseLogs.length > 0) {
            lineItems.push({
                number: String(lineItems.length + 1),
                description: 'Expense Log',
                category: 'Miscellaneous Expenses',
                amount: formatCurrencyValue(metrics.totalExpenseAmount),
            });
        }

        return {
            invoiceNo: getFirstValue(detailData, ['invoice.invoice_no', 'invoice_no', 'invoice_number'], `INV-${tripIdDisplay}`),
            invoiceDate: formatDateOnly(getFirstValue(detailData, ['invoice.invoice_date', 'invoice_date'], new Date())),
            billToName: metaProps.customerName,
            billToAddress: getCustomerAddress(detailData),
            tripDetails: [
                { label: 'Trip ID', value: `Trip ID: ${tripIdDisplay}` },
                { label: 'Vehicle', value: metaProps.vehicleLabel },
                { label: 'Driver', value: metaProps.driverName },
                { label: 'Driver Contact', value: metaProps.driverContact },
                {
                    label: 'Trip Dates',
                    value: [formatDateOnly(actualStartAt, ''), formatDateOnly(actualEndAt, '')].filter(Boolean).join(' -> ') || '--',
                },
                {
                    label: 'Trip Journey',
                    value: firstStop && lastStop ? `${firstStop.location || '--'} -> ${lastStop.location || '--'}` : '--',
                },
            ],
            lineItems,
            subTotal: formatCurrencyValue(subtotalValue),
            taxLabel: taxPercent ? `GST / Tax (${formatNumberValue(taxPercent)}%)` : 'GST / Tax',
            taxAmount: formatCurrencyValue(taxValue),
            grandTotal: formatCurrencyValue(grandTotalValue),
        };
    }, [detailData, expenseLogs.length, fuelLogs.length, metaProps, metrics.fuelCost, metrics.totalExpenseAmount, metrics.totalSpendAmount, routeStops, tripIdDisplay]);

    const financialReviewRows = useMemo(() => {
        const fuelExpense = Number(settlementData?.total_fuel_expense ?? metrics.fuelCost ?? 0);
        const operationalExpense = Number(settlementData?.total_operational_expenses ?? metrics.totalExpenseAmount ?? 0);
        const shortageExpense = Number(settlementData?.total_shortage_expense ?? 0);
        const insuranceExpense = Number(settlementData?.total_insurance_expense ?? 0);
        const netExpenses = Number(settlementData?.trip_net_expenses ?? (fuelExpense + operationalExpense + shortageExpense + insuranceExpense));
        const revenueAmount = Number(settlementData?.invoice_revenue_amount ?? settlementData?.invoice_gross_amount ?? 0);
        const taxAmount = Number(settlementData?.invoice_tax_amount ?? 0);
        const netProfit = Number(settlementData?.trip_net_profit ?? (revenueAmount - netExpenses));

        const rows = [
            { label: 'Total Fuel Expense', value: formatCurrencyValue(fuelExpense) },
            { label: 'Total Operational Expenses', value: formatCurrencyValue(operationalExpense) },
        ];

        if (shortageExpense > 0) {
            rows.push({ label: 'Total Shortage Expense', value: formatCurrencyValue(shortageExpense) });
        }

        if (insuranceExpense > 0) {
            rows.push({ label: 'Total Insurance Expense', value: formatCurrencyValue(insuranceExpense) });
        }

        rows.push({
            label: 'Trip Net Expenses',
            value: formatCurrencyValue(netExpenses),
            separated: true,
        });

        rows.push({
            label: 'Revenue Amount',
            value: formatCurrencyValue(revenueAmount),
        });

        if (taxAmount > 0) {
            rows.push({
                label: 'Tax Amount',
                value: formatCurrencyValue(taxAmount),
            });
        }

        rows.push({
            label: 'Trip Net Profit',
            value: formatCurrencyValue(netProfit),
            emphasis: true,
        });

        return rows;
    }, [settlementData, metrics.fuelCost, metrics.totalExpenseAmount]);

    const isPageLoading = Boolean(
        realTripId && (tripDetailsLoading || tripDetailsRequestPending || loadedTripDetailsId !== realTripId)
    );

    const handleFormSubmit = (_, { setSubmitting }) => {
        setSubmitting(false);
        setIsVerificationMode(true);
        setIsApprovalMode(false);
        setIsInvoicingMode(false);
        setIsApprovalTrailOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleOpenReturnForCorrection = () => {
        if (!canReturnForCorrection) {
            errorNotify("You do not have permission to return trip for correction");
            return;
        }
        setReturnCorrectionModalOpen(true);
    };

    const handleConfirmReturnForCorrection = async (notes) => {
        setReturnCorrectionLoading(true);
        try {
            const tripNumericId = Number(realTripId || detailData?.id || id);
            await apiService.returnTripToCorrection({
                trip_id: tripNumericId,
                note: notes,
            });
            successNotify('Trip returned for correction successfully.');
            setReturnCorrectionModalOpen(false);
            const targetId = realTripId || detailData?.id || id;
            navigate(`/trips-list/in-transit/${targetId}`);
        } catch (error) {
            errorNotify(error?.message || error || 'Failed to return trip for correction');
        } finally {
            setReturnCorrectionLoading(false);
        }
    };

    const handleOpenVerifyTrip = () => {
        if (!canVerifyTrip) {
            errorNotify("You do not have permission to verify trips");
            return;
        }
        setVerifyTripModalOpen(true);
    };

    const handleConfirmVerifyTrip = async () => {
        setVerifyTripLoading(true);
        try {
            const tripNumericId = Number(realTripId || detailData?.id || id);
            await apiService.verifyTripStage({
                trip_id: tripNumericId,
            });
            successNotify('Trip verified successfully.');
            setVerifyTripModalOpen(false);
            setIsApprovalMode(true);
            setIsVerificationMode(true);
            setIsInvoicingMode(false);
            if (realTripId) {
                dispatch(getTripDetails(realTripId));
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            errorNotify(error?.message || error || 'Failed to verify trip');
        } finally {
            setVerifyTripLoading(false);
        }
    };

    const handleOpenReviewedApproved = () => {
        if (!canApproveTrip) {
            errorNotify("You do not have permission to approve trips");
            return;
        }
        setApproveTripModalOpen(true);
        setTripCloseDetailLoading(true);
        setTripCloseDetailData(null);
        const tripNumericId = Number(realTripId || detailData?.id || id);
        if (tripNumericId) {
            apiService
                .getTripSettlementDetail(tripNumericId)
                .then((response) => {
                    setTripCloseDetailData(response?.data || response || null);
                })
                .catch((err) => {
                    console.error("Error fetching trip settlement detail:", err);
                })
                .finally(() => {
                    setTripCloseDetailLoading(false);
                });
        } else {
            setTripCloseDetailLoading(false);
        }
    };

    const handleConfirmReviewedApproved = async () => {
        setApproveTripLoading(true);
        try {
            const tripNumericId = Number(realTripId || detailData?.id || id);
            await apiService.tripClosed({
                trip_id: tripNumericId,
            });
            successNotify('Trip reviewed and approved successfully.');
            setApproveTripModalOpen(false);
            setIsInvoicingMode(true);
            setIsApprovalMode(false);
            setIsVerificationMode(false);
            setIsApprovalTrailOpen(false);
            setFuelViewMode('card');
            setExpenseViewMode('table');
            if (realTripId) {
                dispatch(getTripDetails(realTripId));
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            errorNotify(error?.message || error || 'Failed to approve and close trip');
        } finally {
            setApproveTripLoading(false);
        }
    };

    const handleDownloadCloserReport = async () => {
        if (downloadingReport) return;
        setDownloadingReport(true);
        try {
            const tripNumericId = Number(realTripId || detailData?.id || id);
            const response = await apiService.getTripCloserReport(tripNumericId);
            const fileUrl = response?.data || response;
            if (fileUrl) {
                try {
                    const res = await fetch(fileUrl);
                    const blob = await res.blob();
                    saveAs(blob, `trip-closure-${tripNumericId}.pdf`);
                    successNotify('Report download started successfully.');
                } catch (fetchErr) {
                    saveAs(fileUrl, `trip-closure-${tripNumericId}.pdf`);
                    successNotify('Report download triggered.');
                }
            } else {
                errorNotify('Failed to get report download URL.');
            }
        } catch (error) {
            errorNotify(error?.message || error || 'Failed to download report');
        } finally {
            setDownloadingReport(false);
        }
    };

    const handleOpenGeneratedInvoice = () => {
        handleDownloadCloserReport();
    };

    const handleOpenFuelSheet = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setFuelSheetLoading(true);
        try {
            const response = await apiService.getFuelSheet(tripNumericId);
            const fileUrl = response?.data?.url || response?.url;
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("Fuel sheet URL not found");
            }
        } catch (error) {
            console.error("Error generating fuel sheet:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate fuel sheet");
        } finally {
            setFuelSheetLoading(false);
        }
    };

    const handleOpenBuilty = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setBuiltyLoading(true);
        try {
            const response = await apiService.getBilty(tripNumericId);
            const fileUrl = response?.data?.url || response?.url;
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("Builty URL not found");
            }
        } catch (error) {
            console.error("Error generating builty:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate builty");
        } finally {
            setBuiltyLoading(false);
        }
    };

    const handleOpenPod = async () => {
        const tripNumericId = detailData?.id || realTripId;
        if (!tripNumericId) {
            errorNotify("Trip ID not found");
            return;
        }

        setPodLoading(true);
        try {
            const response = await apiService.getPod(tripNumericId);
            const fileUrl = response?.data?.url || response?.url || (typeof response === "string" ? response : null);
            if (fileUrl) {
                window.open(fileUrl, "_blank");
            } else {
                errorNotify("POD URL not found");
            }
        } catch (error) {
            console.error("Error generating POD:", error);
            errorNotify(typeof error === "string" ? error : error?.message || "Failed to generate POD");
        } finally {
            setPodLoading(false);
        }
    };

    if (isPageLoading) {
        return <SpinnerLoader />;
    }

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={validateClosingForm}
            onSubmit={handleFormSubmit}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit, isSubmitting }) => (
                <Form className={`trip_closing_activity_page ${isVerificationMode ? 'is_verification_mode' : ''} ${isInvoicingMode ? 'is_invoicing_mode' : ''}`.trim()}>
                    <div className="desktop_header_only">
                        <Breadcrumbs values={['Home', 'Trip Management', 'Trip Detail', pageTitle]} />
                        <div className="access_view transit_top_bar completed_top_bar">
                            <h2>{pageTitle}</h2>
                            <div className="transit_top_actions">
                                {canViewBuilty && (
                                    <button
                                        type="button"
                                        className="btn_builty"
                                        onClick={handleOpenBuilty}
                                        disabled={builtyLoading}
                                        title="View Builty PDF"
                                    >
                                        {builtyLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiFileText style={{ fontSize: '14px' }} />
                                                <span>Builty</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                {canViewPod && (
                                    <button
                                        type="button"
                                        className="btn_builty"
                                        onClick={handleOpenPod}
                                        disabled={podLoading}
                                        title="View POD PDF"
                                    >
                                        {podLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FiFileText style={{ fontSize: '14px' }} />
                                                <span>POD</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                {isInvoicingMode ? (
                                    <>
                                        <GenerateInvoiceButton
                                            className="closing_generate_invoice_header_btn"
                                            onClick={handleDownloadCloserReport}
                                            loading={downloadingReport}
                                        />
                                        <Button
                                            cancelBtn={() => navigate(-1)}
                                            cancelBtnText="Back"
                                            shouldRow={false}
                                            isShowSubmitBtn={false}
                                        />
                                    </>
                                ) : isVerificationMode ? (
                                    <div className="closing_verification_actions">
                                        {canReturnForCorrection && (
                                            <Button
                                                iconButton
                                                icon={<FiArrowLeft />}
                                                iconBtnText="Return for Correction"
                                                onClick={handleOpenReturnForCorrection}
                                                type="button"
                                                shouldRow={false}
                                                buttonExtraCss="closing_return_correction_btn"
                                            />
                                        )}
                                        {isApprovalMode ? (
                                            canApproveTrip && (
                                                <Button
                                                    iconButton
                                                    icon={<FiCheckCircle />}
                                                    iconBtnText="Reviewed & Approved"
                                                    onClick={handleOpenReviewedApproved}
                                                    type="button"
                                                    shouldRow={false}
                                                    buttonExtraCss="closing_trip_verified_btn"
                                                />
                                            )
                                        ) : (
                                            canVerifyTrip && (
                                                <Button
                                                    iconButton
                                                    icon={<FiCheckCircle />}
                                                    iconBtnText="Trip Verified"
                                                    onClick={handleOpenVerifyTrip}
                                                    type="button"
                                                    shouldRow={false}
                                                    buttonExtraCss="closing_trip_verified_btn"
                                                />
                                            )
                                        )}
                                        <Button
                                            cancelBtn={() => navigate(-1)}
                                            cancelBtnText="Back"
                                            shouldRow={false}
                                            isShowSubmitBtn={false}
                                        />
                                    </div>
                                ) : canSubmitClosingActivity ? (
                                    <Button
                                        cancelBtn={() => navigate(-1)}
                                        cancelBtnText="Cancel"
                                        loading={isSubmitting}
                                        disabled={isSubmitting}
                                        submitText={isSubmitting ? 'Submitting...' : 'Submit Closing Activity'}
                                        type="submit"
                                        shouldRow={false}
                                        isShowSubmitBtn={true}
                                        submitBtn={handleSubmit}
                                    />
                                ) : (
                                    <Button
                                        cancelBtn={() => navigate(-1)}
                                        cancelBtnText="Back"
                                        shouldRow={false}
                                        isShowSubmitBtn={false}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mobile_header_only mobile_closing_header">
                        <div className="mobile_header_left_controls">
                            <button
                                className="mobile_menu_icon_btn"
                                onClick={() => navigate(-1)}
                                type="button"
                                title="Back"
                            >
                                <FiArrowLeft />
                            </button>
                            <h1 className="mobile_header_title">{pageTitle}</h1>
                        </div>
                    </div>

                    {/* Mobile Builty/POD row — below header, above Verification Summary */}
                    {(canViewBuilty || canViewPod) && (
                        <div className="mobile_header_only mobile_builty_pod_row">
                            {canViewBuilty && (
                                <button
                                    type="button"
                                    className="btn_builty"
                                    onClick={handleOpenBuilty}
                                    disabled={builtyLoading}
                                    title="View Builty PDF"
                                >
                                    {builtyLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiFileText style={{ fontSize: '14px' }} />
                                            <span>Builty</span>
                                        </>
                                    )}
                                </button>
                            )}
                            {canViewPod && (
                                <button
                                    type="button"
                                    className="btn_builty"
                                    onClick={handleOpenPod}
                                    disabled={podLoading}
                                    title="View POD PDF"
                                >
                                    {podLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiFileText style={{ fontSize: '14px' }} />
                                            <span>POD</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    <Row className="closing_main_row g-3">
                        <Col lg={8} md={12} className="closing_left_col">
                            {isInvoicingMode ? (
                                <>
                                    <FinancialReview
                                        tripIdDisplay={tripIdDisplay}
                                        rows={financialReviewRows}
                                    />

                                    <InvoiceRemarksField
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        handleBlur={handleBlur}
                                    />

                                    <div className="invoicing_generate_invoice_inline">
                                        <GenerateInvoiceButton
                                            className="closing_generate_invoice_full_btn"
                                            onClick={handleDownloadCloserReport}
                                            loading={downloadingReport}
                                        />
                                    </div>

                                    <TripHistoryApprovalTrail
                                        isOpen={isApprovalTrailOpen}
                                        onToggle={() => setIsApprovalTrailOpen((previousValue) => !previousValue)}
                                    >
                                        <InvoicingApprovalTrailContent
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            handleChange={handleChange}
                                            handleBlur={handleBlur}
                                            setFieldValue={setFieldValue}
                                            attachments={attachments}
                                            setAttachments={setAttachments}
                                            fuelViewMode={fuelViewMode}
                                            setFuelViewMode={setFuelViewMode}
                                            expenseViewMode={expenseViewMode}
                                            setExpenseViewMode={setExpenseViewMode}
                                            metaProps={metaProps}
                                            routeStops={routeStops}
                                            distanceSummary={distanceSummary}
                                            canViewRouteJourney={canViewRouteJourney}
                                            dispatchSummary={dispatchSummary}
                                            tripCostSummary={tripCostSummary}
                                            fuelLogs={fuelLogs}
                                            expenseLogs={expenseLogs}
                                            fuelRateLabel={fuelRateLabel}
                                            reviewNotes={reviewNotes}
                                            onOpenAttachment={handleOpenAttachmentViewer}
                                            onOpenFuelSheet={handleOpenFuelSheet}
                                            fuelSheetLoading={fuelSheetLoading}
                                            canViewFuelSheet={canViewFuelSheet}
                                        />
                                    </TripHistoryApprovalTrail>
                                </>
                            ) : (
                                <>
                                    <section className="closing_panel closing_trip_summary">
                                <div className="closing_panel_header">
                                    <div>
                                        <h3 className="desktop_summary_title">
                                            {isApprovalMode ? 'Approval Summary' : isVerificationMode ? 'Verification Summary' : 'Trip Summary'}
                                        </h3>
                                        <h3 className="mobile_summary_title">
                                            {isApprovalMode ? 'Approval Summary' : isVerificationMode ? 'Verification Summary' : 'Trip In Progress'}
                                        </h3>
                                        <p className="desktop_summary_subtitle">
                                            {isApprovalMode
                                                ? 'Review the verified trip details and provide final approval before the trip is closed and invoicing begins.'
                                                : isVerificationMode
                                                ? 'Validate operational records, and ensure the trip is ready for approval.'
                                                : 'Read-only recap of everything captured before closing'}
                                        </p>
                                        <p className="mobile_summary_subtitle">
                                            {isApprovalMode
                                                ? 'Review the verified trip details and provide final approval before the trip is closed and invoicing begins.'
                                                : isVerificationMode
                                                ? 'Validate operational records, and ensure the trip is ready for approval.'
                                                : "Keep track of the vehicle's progress until the trip is complete."}
                                        </p>
                                    </div>
                                    <div className="trip_id_green_pill">
                                        Trip ID: {tripIdDisplay}
                                    </div>
                                </div>

                                <div className="closing_panel_body">
                                    {isVerificationMode && <ClosingReviewNotes notes={reviewNotes} />}

                                    <TripMetaGrid includeContractCostCenter {...metaProps} />

                                    {canViewRouteJourney && (
                                        <TripRouteJourney
                                            className="closing_route_journey"
                                            stops={routeStops}
                                            distanceSummary={distanceSummary}
                                        />
                                    )}
                                </div>
                            </section>

                            <section className="closing_section_card">
                                <h3>Dispatch Summary</h3>
                                <div className="closing_summary_grid dispatch_grid">
                                    {dispatchSummary.map((item) => (
                                        <SummaryCard item={item} key={item.title} />
                                    ))}
                                </div>
                            </section>

                            <section className="closing_section_card">
                                <h3>Trip Cost Summary</h3>
                                <div className="closing_summary_grid">
                                    {tripCostSummary.map((item) => (
                                        <SummaryCard item={item} key={item.title} />
                                    ))}
                                </div>
                            </section>

                            <LogSection
                                title="Fuel Log"
                                rate={fuelRateLabel}
                                mode={fuelViewMode}
                                onModeChange={setFuelViewMode}
                                type="fuel"
                                rows={fuelLogs}
                                onOpenAttachment={handleOpenAttachmentViewer}
                                onOpenFuelSheet={handleOpenFuelSheet}
                                fuelSheetLoading={fuelSheetLoading}
                                canViewFuelSheet={canViewFuelSheet}
                            />

                            <LogSection
                                title="Expense Log"
                                mode={expenseViewMode}
                                onModeChange={setExpenseViewMode}
                                type="expense"
                                rows={expenseLogs}
                                onOpenAttachment={handleOpenAttachmentViewer}
                            />

                            <section className="closing_section_card closing_closing_card">
                                <h3>Closing Activity</h3>
                                <div className="closing_fields_grid">
                                    <ClosingField
                                        label="Actual Start Date & Time"
                                        name="actual_start_datetime"
                                        type="text"
                                        value={formatClosingDateTime(values.actual_start_datetime)}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        required
                                        error={errors.actual_start_datetime}
                                        touched={touched.actual_start_datetime}
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Actual End Date & Time"
                                        name="actual_end_datetime"
                                        type="text"
                                        value={formatClosingDateTime(values.actual_end_datetime)}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        required
                                        error={errors.actual_end_datetime}
                                        touched={touched.actual_end_datetime}
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Start Odometer Reading"
                                        name="start_odometer"
                                        value={values.start_odometer}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        required
                                        unit="km"
                                        error={errors.start_odometer}
                                        touched={touched.start_odometer}
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Closing Odometer Reading"
                                        name="closing_odometer"
                                        value={values.closing_odometer}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        required
                                        unit="km"
                                        error={errors.closing_odometer}
                                        touched={touched.closing_odometer}
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Opening Fuel"
                                        name="opening_fuel"
                                        value={values.opening_fuel}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        unit="ltrs"
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Closing Fuel"
                                        name="closing_fuel"
                                        value={values.closing_fuel}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        unit="ltrs"
                                        disabled={true}
                                    />
                                </div>

                                <h4 className="product_shortage_title">Product Shortage</h4>
                                <div className="product_shortage_grid">
                                    <ClosingField
                                        label="Delivered Quantity"
                                        name="delivered_qty"
                                        value={values.delivered_qty || values.delivered || '0.00'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        unit="Items"
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Shortage"
                                        name="shortage"
                                        value={values.shortage}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        unit="Items"
                                        disabled={true}
                                    />
                                    <ClosingField
                                        label="Relief Qty"
                                        name="relief_qty"
                                        value={values.relief_qty}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        unit="Items"
                                        disabled={true}
                                    />
                                </div>
                            </section>

                            <section className="closing_section_card closing_attachments_card">
                                <h3>Trip Attachments</h3>
                                <FileUpload
                                    destination="trip-closing"
                                    files={attachments}
                                    setFiles={setAttachments}
                                    maxFileSize={10}
                                    className="closing_file_upload"
                                    disabled={isVerificationMode}
                                />
                            </section>
                                </>
                            )}
                        </Col>

                        <Col lg={4} md={12} className="closing_right_col">
                            <div className="driver_details_sidebar service_cost_summary_details closing_sidebar_stack">
                                <CostSummary metrics={metrics} />

                                <LogsComments />

                                <div className="trip_notes_main_div mt-3">
                                    <h5>General Notes</h5>
                                    <div style={{ padding: '0 16px 16px 16px' }}>
                                        <Input
                                            label=""
                                            name="general_notes"
                                            placeholder="Type your notes or additional info..."
                                            quill={true}
                                            value={values.general_notes}
                                            setFieldValue={setFieldValue}
                                            onBlur={handleBlur}
                                            required={false}
                                            disabled={isVerificationMode}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {!isInvoicingMode && (
                        <div className="mobile_closing_submit_bar">
                            {isVerificationMode ? (
                                <>
                                    {canReturnForCorrection && (
                                        <Button
                                            iconButton
                                            icon={<FiArrowLeft />}
                                            iconBtnText="Return for Correction"
                                            onClick={handleOpenReturnForCorrection}
                                            type="button"
                                            shouldRow={false}
                                            buttonExtraCss="closing_return_correction_btn"
                                        />
                                    )}
                                    {isApprovalMode ? (
                                        canApproveTrip && (
                                            <Button
                                                iconButton
                                                icon={<FiCheckCircle />}
                                                iconBtnText="Reviewed & Approved"
                                                onClick={handleOpenReviewedApproved}
                                                type="button"
                                                shouldRow={false}
                                                buttonExtraCss="closing_trip_verified_btn"
                                            />
                                        )
                                    ) : (
                                        canVerifyTrip && (
                                            <Button
                                                iconButton
                                                icon={<FiCheckCircle />}
                                                iconBtnText="Trip Verified"
                                                onClick={handleOpenVerifyTrip}
                                                type="button"
                                                shouldRow={false}
                                                buttonExtraCss="closing_trip_verified_btn"
                                            />
                                        )
                                    )}
                                </>
                            ) : (
                                canSubmitClosingActivity && (
                                    <Button
                                        cancelBtn={() => navigate(-1)}
                                        cancelBtnText="Cancel"
                                        loading={isSubmitting}
                                        disabled={isSubmitting}
                                        submitText={isSubmitting ? 'Submitting...' : 'Submit Closing Activity'}
                                        type="submit"
                                        shouldRow={false}
                                        isShowSubmitBtn={true}
                                        submitBtn={handleSubmit}
                                    />
                                )
                            )}
                        </div>
                    )}

                    <GeneratedInvoiceModal
                        show={showGeneratedInvoiceModal}
                        onHide={() => setShowGeneratedInvoiceModal(false)}
                        tripIdDisplay={tripIdDisplay}
                        invoiceData={invoiceData}
                        onDownload={handleDownloadCloserReport}
                        downloading={downloadingReport}
                    />

                    <AttachmentViewerModal
                        show={showAttachmentViewerModal}
                        files={attachmentViewerFiles}
                        heading="Attachments"
                        handleClose={() => {
                            setShowAttachmentViewerModal(false);
                            setAttachmentViewerFiles([]);
                        }}
                    />

                    <ReturnForCorrectionModal
                        open={returnCorrectionModalOpen}
                        onClose={() => setReturnCorrectionModalOpen(false)}
                        onConfirm={handleConfirmReturnForCorrection}
                        tripIdDisplay={tripIdDisplay}
                        loading={returnCorrectionLoading}
                    />

                    <StatusModal
                        open={verifyTripModalOpen}
                        onClose={() => setVerifyTripModalOpen(false)}
                        onConfirm={handleConfirmVerifyTrip}
                        status="verified"
                        loading={verifyTripLoading}
                        titleText="Verify this trip?"
                        description={`This will change Trip ${tripIdDisplay} stage to Verified and advance to Approval Summary.`}
                        confirmText="Trip Verified"
                        cancelText="Cancel"
                        centered={true}
                    />

                    <TripCloseModal
                        open={approveTripModalOpen}
                        onClose={() => setApproveTripModalOpen(false)}
                        onConfirm={handleConfirmReviewedApproved}
                        loading={approveTripLoading}
                        detailLoading={tripCloseDetailLoading}
                        detailData={tripCloseDetailData}
                        tripContext={detailData}
                        tripIdDisplay={tripIdDisplay}
                    />
                </Form>
            )}
        </Formik>
    );
};

export default TripClosingActivity;
